import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useReducedMotion } from '../kit'

// One tooltip for every chart on a page.
//
// # WHY ONE, AND WHY FIXED
//
// A tooltip per chart is a stack of absolutely positioned boxes that clip at the
// edge of whichever card they sit in. One fixed box, placed beside the pointer
// and kept inside the window, is what the approved proposal does and what never
// clips. Keyboard focus places it beside the focused mark instead, so a person
// who tabs through a chart reads exactly what a person pointing at it reads.
//
// # VALUES LEAD
//
// A row is a short line key in the series colour, the series name in secondary
// ink and the value in strong ink. Every string goes in as text, never as markup:
// series and vendor names come from the register.

export type TipRow = { label: string; value: string; color?: string }
type Tip = { title: string; rows: TipRow[]; x: number; y: number } | null

type Api = {
  // At the pointer.
  show: (e: { clientX: number; clientY: number }, title: string, rows: TipRow[]) => void
  // Beside an element, for keyboard focus.
  showAt: (el: Element, title: string, rows: TipRow[]) => void
  hide: () => void
}

const noop: Api = { show: () => {}, showAt: () => {}, hide: () => {} }
const Ctx = createContext<Api>(noop)

export function useTooltip(): Api {
  return useContext(Ctx)
}

const TIP_W = 180
const TIP_H = 90

function place(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.max(8, Math.min(x + 14, window.innerWidth - TIP_W)),
    y: Math.max(8, Math.min(y + 14, window.innerHeight - TIP_H)),
  }
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  const [tip, setTip] = useState<Tip>(null)
  const [on, setOn] = useState(false)
  const last = useRef<Tip>(null)

  const api = useMemo<Api>(
    () => ({
      show: (e, title, rows) => {
        const p = place(e.clientX, e.clientY)
        const next = { title, rows, ...p }
        last.current = next
        setTip(next)
        setOn(true)
      },
      showAt: (el, title, rows) => {
        const r = el.getBoundingClientRect()
        const p = place(r.left + r.width / 2, r.top + r.height / 2)
        const next = { title, rows, ...p }
        last.current = next
        setTip(next)
        setOn(true)
      },
      hide: () => setOn(false),
    }),
    [],
  )

  // A page that scrolls under a tooltip leaves it pointing at nothing.
  useEffect(() => {
    const off = () => setOn(false)
    window.addEventListener('scroll', off, { passive: true })
    return () => window.removeEventListener('scroll', off)
  }, [])

  const shown = tip ?? last.current
  // In a portal on the body, because a fixed box inside a page that animates in
  // with a transform is fixed to that page instead of the window, and there it
  // widens the page when it sits near an edge. It carries .hc so the chart
  // tokens reach it outside the host, and it has no position while hidden.
  const box = (
    <div
      className={'hc hc-tip' + (on ? ' on' : '')}
      role="tooltip"
      aria-hidden={!on}
      style={shown && on ? { left: shown.x, top: shown.y } : { left: 0, top: 0, visibility: 'hidden' }}
    >
      {shown && (
        <>
          <div className="t">{shown.title}</div>
          {shown.rows.map((r, i) => (
            <div className="row" key={i}>
              <span>
                {r.color && <i style={{ background: r.color }} />}
                {r.label}
              </span>
              <b>{r.value}</b>
            </div>
          ))}
        </>
      )}
    </div>
  )
  return (
    <Ctx.Provider value={api}>
      {children}
      {typeof document !== 'undefined' ? createPortal(box, document.body) : box}
    </Ctx.Provider>
  )
}

// True once the first frame has painted, so a mark can transition from its
// empty state to its value the way the proposal draws on load. With reduced
// motion it is true from the start and every mark draws in its final state.
export function useArrived(): boolean {
  const reduced = useReducedMotion()
  const [arrived, setArrived] = useState(reduced)
  useEffect(() => {
    if (reduced) {
      setArrived(true)
      return
    }
    let b = 0
    const a = requestAnimationFrame(() => {
      b = requestAnimationFrame(() => setArrived(true))
    })
    return () => {
      cancelAnimationFrame(a)
      cancelAnimationFrame(b)
    }
  }, [reduced])
  return arrived
}

// The rendered width of an element, so an SVG can be drawn in real pixels.
// The proposal stretched a fixed viewBox, which widens round dots into ovals
// and text into a smear on a wide card; drawing at the measured width keeps
// every mark the shape the proposal intended at any width.
export function useWidth<T extends Element>(fallback: number): [(el: T | null) => void, number] {
  const [width, setWidth] = useState(fallback)
  const observer = useRef<ResizeObserver | null>(null)
  const ref = useCallback((el: T | null) => {
    observer.current?.disconnect()
    if (!el) return
    const measure = () => {
      const w = el.getBoundingClientRect().width
      if (w > 0) setWidth(Math.round(w))
    }
    measure()
    if (typeof ResizeObserver !== 'undefined') {
      observer.current = new ResizeObserver(measure)
      observer.current.observe(el)
    }
  }, [])
  useEffect(() => () => observer.current?.disconnect(), [])
  return [ref, width]
}

// Opens a console address the way a link does. Chart marks that cannot be
// anchors (a column in an SVG plot) use this on Enter and on click.
export function go(href: string | undefined): void {
  if (href) window.location.hash = href.replace(/^#/, '')
}
